import * as THREE from "three";
import { MathUtils, Vector2, Vector3 } from "three";
import { fabricColor, fabricCoverNormal, fabricNormal, } from "../../assets/materials";
import { ModelSkeleton } from "./ModelSkeleton";
var degToRad = MathUtils.degToRad;
import { ExtraType } from "../../../entities/LedExtras";
export class CoralModelSkeleton extends ModelSkeleton {
    /**
     * Creates a bezier curve to adjust the shape extrude point along a path. This returns the curve v3 points
     * **/
    createGeometryCurve(size, divisionEnd, divisionMiddle) {
        const startPosY = new THREE.Vector3(0, 0, 0);
        const endPossY = new THREE.Vector3(size, 0, size / divisionEnd);
        const middlePosY = new THREE.Vector3(size / 2, 0, size / divisionMiddle);
        const curve = new THREE.QuadraticBezierCurve3(startPosY, middlePosY, endPossY);
        return curve;
    }
    /**
     * Creates and returns a 2d shape, with extrude along the bezier curve
     * **/
    createShape(shapeLength, shapeHeight, geometryCurve, divisionMiddle = 6, divisionEnd = 6) {
        // Draw the square in 2d
        const shape = new THREE.Shape();
        shape.moveTo(0, 0);
        shape.lineTo(0, shapeHeight);
        shape.lineTo(shapeLength, shapeHeight);
        shape.lineTo(shapeLength, 0);
        shape.lineTo(0, 0);
        // Extrude settings to make the 2d shape 3d decrease steps to reduce polygons if needed
        const extrudeSettings = {
            steps: 50,
            extrudePath: this.createGeometryCurve(geometryCurve, divisionMiddle, divisionEnd),
        };
        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
        return geometry;
    }
    /** calculates the rotationY of the bend object from the slope.
     * This is needed for the rails to be in the right position around the front rails
     * **/
    getRotationY(slopePercent) {
        const z = slopePercent / 15 - Math.round(slopePercent / 15);
        if (z === 0)
            return 0;
        return z * -10;
    }
    /** Create the meshes of the curve rails**/
    createCurvedRails(pos, size, rotateY = 0, rotateZ = 0, slope, edgeHeight, edgeWidth, slopePercent) {
        const pillarTextureRepeat = new Vector2(0.01, 0.01);
        const edgeMat = this.scene.materialPicker.getMaterial(pillarTextureRepeat);
        const mesh = new THREE.Mesh(this.createShape(edgeWidth, edgeHeight, size.x), [edgeMat, edgeMat]);
        // Recalculate the pivot position to center. With extrude it's not working automatically
        mesh.geometry.center();
        mesh.castShadow = true;
        mesh.position.copy(pos);
        mesh.rotateX(MathUtils.degToRad(-90));
        mesh.rotateZ(MathUtils.degToRad(180));
        mesh.rotateY(MathUtils.degToRad(this.getRotationY(slopePercent)));
        this.scene.threeScene.add(mesh);
    }
    /** Metal fabric cover for the roof. Param fabricCoverProjection is calculated dynamically **/
    createCurvedFabricCover(slopeDegree, slope, position, fabricCoverProjection, slopePercent) {
        const pillarTextureRepeat = new Vector2(0.005, 0.05);
        const fabricCoverMat = this.scene.materialPicker.getMaterial(pillarTextureRepeat, fabricCoverNormal);
        const mesh = new THREE.Mesh(this.createShape(this.data.measuredModel.width - 5, 2, fabricCoverProjection + 10, 14, 18), fabricCoverMat);
        // Recalculate the pivot position to center. With extrude it's not working automatically
        mesh.geometry.center();
        mesh.position.set(0 - this.data.measuredModel.projection / 2 + fabricCoverProjection / 2, 260 + this.data.measuredModel.model.edgeHeight + slope - (slope / this.scene.formData.measuredModel.projection) / slopePercent + 5, position.z);
        mesh.rotateX(MathUtils.degToRad(-90));
        mesh.rotateZ(MathUtils.degToRad(180));
        mesh.rotateY(MathUtils.degToRad(this.getRotationY(slopePercent)));
        mesh.rotateY(MathUtils.degToRad(1.3));
        this.scene.threeScene.add(mesh);
    }
    /** Cross panels below the fabric (roof) **/
    createCrossRails(pos, length, shapeHeight, divisionMiddle = 6, divisionEnd = 6, slope, slopePercent) {
        const startingPos = pos.x + this.scene.formData.measuredModel.projection / 2;
        const div = length / 100;
        const positions = [];
        const points = this.createGeometryCurve(length, 6, 6);
        const pillarTextureRepeat = new Vector2(length / 1024 * 24, 3 / 1024 * 24);
        const mat = this.scene.materialPicker.getMaterial(pillarTextureRepeat);
        const mesh = new THREE.InstancedMesh(new THREE.BoxGeometry(6, 3, this.scene.formData.measuredModel.width - this.scene.formData.measuredModel.model.edgeWidth * 2), mat, div + 1);
        const scale = new THREE.Vector3(1, 1, 1);
        const rot = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, degToRad(-10)));
        const rotationY = this.getRotationY(slopePercent);
        for (let i = 1; i < div; i++) {
            const finalPos = new THREE.Vector3().copy(pos);
            finalPos.x = startingPos - this.scene.formData.measuredModel.projection / div * i;
            finalPos.y = points.getPoint(i / div).z + pos.y - points.getPoint(1).z / 2 + slope / 2 + this.scene.formData.measuredModel.model.edgeHeight - 2;
            mesh.setMatrixAt(i, new THREE.Matrix4().compose(finalPos, rot, scale));
            positions.push(finalPos);
        }
        mesh.geometry.center();
        this.scene.pvcLedLights.createPVCLights(positions, new THREE.Color(0xFFFFFF), rotationY, true);
        mesh.rotateZ(MathUtils.degToRad(this.getRotationY(slopePercent)));
        this.scene.threeScene.add(mesh);
    }
    createCurvedFabric(slopeDegree, slope, pos, slopePercent) {
        const posY = pos.y + slope / 2 + this.scene.formData.measuredModel.model.edgeHeight + 2;
        this.createCrossRails(pos, this.data.measuredModel.projection, 5, 6, 6, slope, slopePercent);
        const fabricMat = this.scene.materialPicker.getMaterial(undefined, undefined, false, 0.2);
        let map = new THREE.Texture();
        this.scene.assignTexture(map, fabricColor);
        map.repeat = new Vector2(0.008, 0.008);
        fabricMat.map = map;
        fabricMat.normalMap = fabricNormal;
        fabricMat.roughness = 0.3;
        const mesh = new THREE.Mesh(this.createShape(this.data.measuredModel.width - 5, 2, this.data.measuredModel.projection), [fabricMat, fabricMat]);
        // Recalculate the pivot position to center. With extrude it's not working automatically
        mesh.geometry.center();
        mesh.castShadow = true;
        mesh.rotateX(MathUtils.degToRad(-90));
        mesh.rotateZ(MathUtils.degToRad(180));
        mesh.rotateY(MathUtils.degToRad(this.getRotationY(slopePercent)));
        fabricMat.metalness = 0.5;
        mesh.position.set(0, posY, pos.z);
        mesh.name = "farbric_roof";
        this.scene.threeScene.add(mesh);
    }
    createModule(pos, modulePlace) {
        const fabricCoverProjection = this.data.measuredModel.projection / 47 * 7 + 30;
        const moduleWidth = this.data.measuredModel.width;
        const moduleProjection = this.data.measuredModel.projection;
        const edgeWidth = this.data.measuredModel.model.edgeWidth;
        const edgeHeight = this.data.measuredModel.model.edgeHeight;
        const railCenterQty = Math.ceil(moduleWidth / 450);
        const railQty = railCenterQty + 1;
        const slopePercent = moduleWidth / railCenterQty / 25 - 1;
        const slope = slopePercent / 100 * moduleProjection;
        const slopeDegree = Math.round(Math.atan(slopePercent / 100) * 180 / Math.PI * 10000) / 10000;
        // Roof
        this.createCurvedFabricCover(slopeDegree, slope, pos, fabricCoverProjection, slopePercent);
        this.createCurvedFabric(slopeDegree, slope, pos, slopePercent);
        const railSize = new Vector3(moduleProjection + edgeWidth * 2, edgeHeight, edgeWidth);
        const frontPillarSize = new Vector3(edgeWidth, 260, edgeWidth);
        const backPillarSize = new Vector3(edgeWidth, 260 + slope + edgeHeight, edgeWidth);
        const frontBeamSize = new Vector3(moduleWidth + 20, edgeHeight, edgeWidth);
        const backBeamSize = new Vector3(moduleWidth, edgeHeight, edgeWidth);
        const sideBeamSize = new Vector3(moduleProjection, edgeHeight, edgeWidth);
        //Back beam
        this.createEdge(new Vector3(pos.x - moduleProjection / 2, pos.y + slope + edgeHeight, pos.z), backBeamSize, 90);
        // Second back beam when glass is present
        if (this.scene.formData.far.extra !== ExtraType.NONE)
            this.createEdge(new Vector3(pos.x - moduleProjection / 2, pos.y, pos.z), backBeamSize, 90);
        //Front beam
        this.createEdge(new Vector3(pos.x + moduleProjection / 2, pos.y, pos.z), frontBeamSize, 90);
        // Projection side extra leg when it's more then 700
        const edgeW = this.scene.formData.measuredModel.model.edgeWidth;
        //Rails & Pillars
        for (let i = 0; i < railQty; i++) {
            let adjust = 0;
            if (i == 0) {
                adjust = edgeWidth / 2;
            }
            else if (i == railQty - 1) {
                adjust = -edgeWidth / 2;
            }
            //RAILS
            const railposX = pos.x + edgeWidth / 1.5;
            const railposY = pos.y + slope / 2 + edgeHeight - 0.7;
            const railposZ = pos.z + (moduleWidth / railCenterQty * i) - moduleWidth / 2 + adjust;
            const railpos = new Vector3(railposX, railposY, railposZ);
            this.createCurvedRails(railpos, railSize, 0, -slopeDegree, slope, edgeHeight, edgeWidth, slopePercent);
            //FRONT PILLARS
            const frontPillarposX = pos.x + moduleProjection / 2;
            const frontPillarposY = pos.y - frontPillarSize.y / 2;
            const frontPillarposZ = pos.z + (moduleWidth / railCenterQty * i) - moduleWidth / 2 + adjust;
            const frontPillarpos = new Vector3(frontPillarposX, frontPillarposY, frontPillarposZ);
            if (i === 0 && modulePlace === 1 && this.scene.formData.right.wall === true) {
            }
            else if (this.data.measuredModel.modulesZ === modulePlace && i === railQty - 1 && this.scene.formData.left.wall === true) {
            }
            else {
                this.createEdge(frontPillarpos, frontPillarSize, 0);
            }
            // Sidebeams and projection side extra leg when it's more than 700
            if (this.data.freestanding === true || this.data.left.extra !== ExtraType.NONE) {
                // Middle projection pillar
                if (this.data.projectionValue > 700)
                    this.createEdge(new THREE.Vector3(this.scene.dimensions.left.position.x, 130, this.scene.dimensions.left.position.z), new THREE.Vector3(edgeW, 260, edgeW));
                // Left sidebeam
                this.createEdge(new Vector3(pos.x, pos.y, pos.z + moduleWidth - moduleWidth / 2 - edgeWidth / 2), sideBeamSize, 0);
            }
            if (this.data.freestanding === true || this.data.right.extra !== ExtraType.NONE) {
                // Middle projection pillar
                if (this.data.projectionValue > 700)
                    this.createEdge(new THREE.Vector3(this.scene.dimensions.right.position.x, 130, this.scene.dimensions.right.position.z), new THREE.Vector3(edgeW, 260, edgeW));
                // Right sidebeam
                this.createEdge(new Vector3(pos.x, pos.y, pos.z - moduleWidth / 2 + edgeWidth / 2), sideBeamSize, 0);
            }
            //BACK PILLARS
            if (this.data.freestanding === true) {
                const backPillarposX = pos.x - moduleProjection / 2;
                const backPillarposY = backPillarSize.y / 2;
                const backPillarposZ = pos.z + (moduleWidth / railCenterQty * i) - moduleWidth / 2 + adjust;
                const backPillarpos = new Vector3(backPillarposX, backPillarposY, backPillarposZ);
                this.createEdge(backPillarpos, backPillarSize, 0);
            }
        }
    }
}
